import React, {
  useCallback, useEffect, useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, ButtonBar } from '../../components/basic-inputs';
import { VaultMessage } from '../../types/vault';

import { vaultClient } from './vault-client';

export function VaultIndex() {
  const [ messages, setMessages ] = useState<VaultMessage[]>([]);
  const [ loading, setLoading ] = useState(true);
  const [ error, setError ] = useState<string | null>(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await vaultClient.findAll();
      setMessages(data);
    } catch (err) {
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
    console.log('handleDelete', event);
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRecipientsText = (recipients: VaultMessage['recipients']) => {
    if (recipients.length === 0) return 'No recipients';
    if (recipients.length === 1) return recipients[0].firstName + ' ' + recipients[0].lastName;
    return `${recipients.length} recipients`;
  };
  
  const handleView = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    console.log('handleView', event);
  }, []);

  if (loading) {
    return (
      <div className="vault-page">
        <div className="screen-container">
          <div className="screen-header">
            <div className="screen-title">
              <h2>Secret Vault</h2>
              <p>Your private messages for loved ones</p>
            </div>
          </div>
          <div className="screen-content">
            <div className="loading-container">
              <p>Loading messages...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vault-page">
        <div className="screen-container">
          <div className="screen-header">
            <div className="screen-title">
              <h2>Secret Vault</h2>
              <p>Your private messages for loved ones</p>
            </div>
          </div>
          <div className="screen-content">
            <div className="error-container">
              <p className="error-message">{error}</p>
              <button className="button" onClick={loadMessages}>Retry</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vault-page">
      <div className="screen-container">
        <div className="screen-header">
          <div className="screen-title">
            <h2>Secret Vault</h2>
            <p>Your private messages for loved ones</p>
          </div>
        </div>
        
        <div className="screen-content">
          <Button style={{ textAlign: 'center' }} fullWidth>
                Create Message
          </Button>

          {messages.length === 0 ? (
            <div className="no-messages-display">
              <div className="no-messages-icon">
                <span className="material-symbols-outlined">lock</span>
              </div>
              <h2>No Secret Messages Yet</h2>
              <p>Create your first secret message for your loved ones</p>
              <Button fullWidth>
                Create Message
              </Button>
            </div>
          ) : (
            <div className="vault-messages-list">
              {messages.map((message) => (
                <div key={message.id} className="vault-message-card">
                  <div className="message-header">
                    <div className="message-info">
                      <h3 className="message-title">{message.title}</h3>
                      <p className="message-recipients">{getRecipientsText(message.recipients)}</p>
                      <p className="message-date">Created {formatDate(message.createdAt)}</p>
                    </div>
                    <div className="message-status">
                      {message.isLocked ? (
                        <span className="lock-status locked">
                          <span className="material-symbols-outlined">lock</span>
                          Locked
                        </span>
                      ) : (
                        <span className="lock-status unlocked">
                          <span className="material-symbols-outlined">lock_open</span>
                          Unlocked
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="message-preview">
                    <p>{message.content.substring(0, 100)}...</p>
                  </div>

                  <ButtonBar
                    handlePrimary={handleView}
                    primaryText="View"
                    handleSecondary={handleDelete}
                    secondaryText="Delete"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
